// screens/create-product-mgt/AllocateIngredients.jsx
import React, { useState, useEffect } from 'react';
import {
    Form,
    Row,
    Col,
    Button,
    Card,
    Alert,
    Spinner,
    Badge,
    Modal
} from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import axios from '../../api/axios';

const AllocateIngredients = ({
    data,
    onNext,
    onBack,
    updateData,
    currentStep,
    isSubmitting,
    setIsSubmitting,
    setErrors,
    onComplete
}) => {
    const accessToken = localStorage.getItem("authToken");

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [categoryList, setCategoryList] = useState([]);
    const [addons, setAddons] = useState([]);
    const [rawMaterialList, setRawMaterialList] = useState([]);
    const [showSkipConfirm, setShowSkipConfirm] = useState(false);
    const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
    const [existingIngredients, setExistingIngredients] = useState(null);

    const getDefaultConditionalAddon = () => ({
        addonId: '',
        addonValueId: '',
        quantity: ''
    });

    const getDefaultRawMaterial = () => ({
        rawMaterial: '',
        quantity: '',
        unit: '',
        addonId: '',
        addonValueId: '',
        isImportant: false,
        isOptional: false,
        conditionalAddons: []
    });

    const [formData, setFormData] = useState({
        product: '',
        rawMaterials: [getDefaultRawMaterial()]
    });

    const productData = data.product || {};
    const productId = productData._id;

    useEffect(() => {
        getCategoryList();
        getAddons();
        getRawMaterials();

        if (productId) {
            setFormData(prev => ({
                ...prev,
                product: productId
            }));
            fetchExistingIngredients();
        }
    }, [productId]);

    const getCategoryList = async () => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };
            const response = await axios.get("masters/category/get", { headers });

            if (response?.data?.status === true) {
                setCategoryList(response?.data?.data?.categories);
            }
        } catch (error) {
            toast.error("Failed to load categories", {
                position: "top-right",
                autoClose: 3000,
                theme: "light",
            });
        }
    };

    const getAddons = async () => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`
            };
            const response = await axios.get("addons?page=1&limit=1000", { headers });
            setAddons(response?.data?.data?.addons || []);
        } catch (error) {
            console.error("Error fetching addons:", error);
        }
    };

    const getRawMaterials = async () => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };
            const response = await axios.get("raw-material", { headers });

            if (response?.data?.status === true) {
                const materials = response?.data?.data?.rawMaterials || [];
                materials.forEach((item, index) => {
                    item.serialNumber = index + 1;
                });
                setRawMaterialList(materials);
            }
        } catch (error) {
            toast.error("Failed to load raw materials", {
                position: "top-right",
                autoClose: 3000,
                theme: "light",
            });
        }
    };

    const fetchExistingIngredients = async () => {
        if (!productId) return;

        try {
            setIsLoading(true);
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };

            const response = await axios.get(`recipe?product=${productId}`, { headers });

            if (response?.data?.status === true && response?.data?.data?.recieps?.length > 0) {
                const existingRecipe = response.data.data.recieps[0];
                setExistingIngredients(existingRecipe);

                const mappedRawMaterials = existingRecipe?.rawMaterials?.map(item => ({
                    _id: item?._id,
                    rawMaterial: item?.rawMaterial?._id || '',
                    quantity: item?.quantity || '',
                    unit: item?.rawMaterial?.unit || '',
                    addonId: item?.addonId?._id || '',
                    addonValueId: item?.addonValueId?._id || item?.addonValueId || '',
                    isImportant: item?.isImportant || false,
                    isOptional: item?.isOptional || false,
                    conditionalAddons: (item?.conditionalAddons || []).map(cond => ({
                        addonId: cond?.addonId?._id || cond?.addonId || '',
                        addonValueId: cond?.addonValueId?._id || cond?.addonValueId || '',
                        quantity: cond?.quantity || ''
                    }))
                })) || [getDefaultRawMaterial()];

                setFormData({
                    _id: existingRecipe?._id,
                    product: productId,
                    rawMaterials: mappedRawMaterials.length ? mappedRawMaterials : [getDefaultRawMaterial()]
                });

                toast.info('Existing ingredients loaded. You can update them.', {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "light",
                });
            }
        } catch (error) {
            console.error('Error fetching existing ingredients:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRawMaterialChange = (index, field, value) => {
        const updatedRawMaterials = [...formData.rawMaterials];
        updatedRawMaterials[index] = {
            ...updatedRawMaterials[index],
            [field]: value
        };

        if (field === 'rawMaterial' && value) {
            const selectedMaterial = rawMaterialList.find(rm => rm._id === value);
            updatedRawMaterials[index].unit = selectedMaterial?.unit || "";
        }

        if (field === 'rawMaterial' && !value) {
            updatedRawMaterials[index].unit = "";
        }

        if (field === 'addonId') {
            updatedRawMaterials[index].addonValueId = "";
            updatedRawMaterials[index].conditionalAddons = [];
        }

        setFormData(prev => ({
            ...prev,
            rawMaterials: updatedRawMaterials
        }));
    };

    const addRawMaterialEntry = () => {
        setFormData(prev => ({
            ...prev,
            rawMaterials: [...prev.rawMaterials, getDefaultRawMaterial()],
        }));
    };

    const removeRawMaterialEntry = (index) => {
        setFormData(prev => {
            const newRawMaterials = [...prev.rawMaterials];
            newRawMaterials.splice(index, 1);

            if (newRawMaterials.length === 0) {
                newRawMaterials.push(getDefaultRawMaterial());
            }

            return {
                ...prev,
                rawMaterials: newRawMaterials
            };
        });
    };

    const addConditionalAddon = (rawMaterialIndex) => {
        setFormData(prev => {
            const updatedRawMaterials = [...prev.rawMaterials];
            updatedRawMaterials[rawMaterialIndex].conditionalAddons = [
                ...(updatedRawMaterials[rawMaterialIndex].conditionalAddons || []),
                getDefaultConditionalAddon()
            ];

            return {
                ...prev,
                rawMaterials: updatedRawMaterials
            };
        });
    };

    const removeConditionalAddon = (rawMaterialIndex, conditionalIndex) => {
        setFormData(prev => {
            const updatedRawMaterials = [...prev.rawMaterials];
            const updatedConditionalAddons = [
                ...(updatedRawMaterials[rawMaterialIndex].conditionalAddons || [])
            ];

            updatedConditionalAddons.splice(conditionalIndex, 1);
            updatedRawMaterials[rawMaterialIndex].conditionalAddons = updatedConditionalAddons;

            return {
                ...prev,
                rawMaterials: updatedRawMaterials
            };
        });
    };

    const handleConditionalAddonChange = (rawMaterialIndex, conditionalIndex, field, value) => {
        setFormData(prev => {
            const updatedRawMaterials = [...prev.rawMaterials];
            const updatedConditionalAddons = [
                ...(updatedRawMaterials[rawMaterialIndex].conditionalAddons || [])
            ];

            updatedConditionalAddons[conditionalIndex] = {
                ...updatedConditionalAddons[conditionalIndex],
                [field]: value
            };

            if (field === 'addonId') {
                updatedConditionalAddons[conditionalIndex].addonValueId = '';
            }

            updatedRawMaterials[rawMaterialIndex].conditionalAddons = updatedConditionalAddons;

            return {
                ...prev,
                rawMaterials: updatedRawMaterials
            };
        });
    };

    const validateForm = () => {
        if (!productId) {
            toast.error('Product not created yet. Please go back to Step 1.', { position: 'top-right' });
            return false;
        }

        const hasValidEntries = formData.rawMaterials.some(rm => rm.rawMaterial);
        if (!hasValidEntries) {
            toast.error('Please add at least one raw material or skip ingredients', { position: 'top-right' });
            return false;
        }

        for (let i = 0; i < formData.rawMaterials.length; i++) {
            const rm = formData.rawMaterials[i];

            if (!rm.rawMaterial && !rm.quantity) {
                continue;
            }

            if (!rm.rawMaterial) {
                toast.error(`Please select a raw material for entry ${i + 1}`, { position: 'top-right' });
                return false;
            }

            if (!rm.quantity || parseFloat(rm.quantity) <= 0) {
                toast.error(`Please enter a valid quantity for entry ${i + 1}`, { position: 'top-right' });
                return false;
            }

            if ((rm.addonId && !rm.addonValueId) || (!rm.addonId && rm.addonValueId)) {
                toast.error(`Please select both addon group and addon value for entry ${i + 1}`, {
                    position: 'top-right'
                });
                return false;
            }

            for (let j = 0; j < (rm.conditionalAddons || []).length; j++) {
                const cond = rm.conditionalAddons[j];

                if (!cond.addonId || !cond.addonValueId || !cond.quantity || parseFloat(cond.quantity) <= 0) {
                    toast.error(`Please complete conditional addon #${j + 1} for entry ${i + 1}`, {
                        position: 'top-right'
                    });
                    return false;
                }
            }
        }

        return true;
    };

    const handleSkipIngredients = () => {
        setShowSkipConfirm(true);
    };

    const confirmSkipIngredients = async () => {
        setShowSkipConfirm(false);

        if (!productId) {
            toast.error('Product not created yet. Please go back to Step 1.', { position: 'top-right' });
            return;
        }

        setIsSaving(true);
        setIsSubmitting(true);

        try {
            updateData('ingredients', {
                items: [],
                payload: null,
                totalIngredients: 0,
                isAllocated: false,
                skipped: true
            });

            toast.success('Product ingredients step skipped!', {
                position: "top-right",
                autoClose: 3000,
                theme: "light",
            });

            if (onComplete) {
                onComplete();
            }
        } catch (error) {
            console.error('Error skipping ingredients:', error);
            toast.error('Failed to skip ingredients', {
                position: "top-right",
                autoClose: 3000,
                theme: "light",
            });
        } finally {
            setIsSaving(false);
            setIsSubmitting(false);
        }
    };

    const handleSaveOrUpdateIngredients = async () => {
        if (!validateForm()) return;

        const validRawMaterials = formData.rawMaterials.filter(
            rm => rm.rawMaterial && rm.quantity && parseFloat(rm.quantity) > 0
        );

        if (validRawMaterials.length === 0) {
            toast.error('Please add at least one valid raw material', { position: 'top-right' });
            return;
        }

        const payload = {
            product: productId,
            rawMaterials: validRawMaterials.map(rm => ({
                rawMaterial: rm.rawMaterial,
                quantity: Number(rm.quantity),
                unit: rm.unit,
                addonId: rm.addonId || null,
                addonValueId: rm.addonValueId || null,
                isImportant: rm.isImportant || false,
                isOptional: rm.isOptional || false,
                conditionalAddons: (rm.conditionalAddons || []).map(cond => ({
                    addonId: cond.addonId,
                    addonValueId: cond.addonValueId,
                    quantity: Number(cond.quantity)
                }))
            }))
        };

        if (formData._id) {
            payload._id = formData._id;
        }

        payload.rawMaterials = payload.rawMaterials.map(item => ({
            ...item,
            addonId: item.addonId?.toString().trim() === "" ? null : item.addonId,
            addonValueId: item.addonValueId?.toString().trim() === "" ? null : item.addonValueId,
            conditionalAddons: (item.conditionalAddons || []).map(cond => ({
                ...cond,
                addonId: cond.addonId?.toString().trim() === "" ? null : cond.addonId,
                addonValueId: cond.addonValueId?.toString().trim() === "" ? null : cond.addonValueId
            }))
        }));

        setIsUpdating(true);
        setIsSubmitting(true);

        try {
            const headers = {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`
            };

            const endpoint = "recipe";
            const method = payload._id ? 'put' : 'post';

            const response = await axios[method](endpoint, payload, { headers });

            if (response?.data?.status === true) {
                const message = payload._id ? 'Ingredients updated successfully!' : 'Ingredients saved successfully!';

                toast.success(message, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "light",
                });

                updateData('ingredients', {
                    items: validRawMaterials,
                    payload,
                    totalIngredients: validRawMaterials.length,
                    isAllocated: true,
                    skipped: false,
                    recipeId: response.data.data?.dbResponse?._id || formData._id
                });

                setShowCompleteConfirm(true);
            } else {
                toast.error(response?.data?.error || 'Failed to save ingredients', {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "light",
                });
            }
        } catch (error) {
            console.error('Error saving/updating ingredients:', error);

            let errorMessage = 'Failed to save ingredients';
            if (error.response) {
                if (error.response.status === 404) {
                    errorMessage = error.response.data.error?.message || error.response.data.error;
                } else if (error.response.status === 400) {
                    errorMessage = error.response.data.data?.message || error.response.data.error;
                } else if (error.response.status === 406) {
                    errorMessage = error.response.data.data?.message || error.response.data.error;
                } else if (error.response.status === 412) {
                    errorMessage = error.response.data.data?.message || error.response.data.error;
                } else if (error.response.status === 500) {
                    errorMessage = error.response.data.error;
                }
            }

            toast.error(errorMessage, {
                position: "top-right",
                autoClose: 3000,
                theme: "light",
            });
            setErrors([errorMessage]);
        } finally {
            setIsUpdating(false);
            setIsSubmitting(false);
        }
    };

    const confirmComplete = async () => {
        setShowCompleteConfirm(false);
        if (onComplete) {
            onComplete();
        }
    };

    const getAddonValueOptions = (addonId) => {
        if (!addonId) return [];
        const addon = addons.find(a => a._id === addonId);
        return addon?.addonValues?.map(val => ({
            value: val._id,
            label: val.value
        })) || [];
    };

    const rawMaterialOptions = rawMaterialList?.map(rm => ({
        value: rm._id,
        label: `${rm.name} (${rm.unit}) ${rm.inStock === false ? " - Out of stock" : ""}`
    }));

    const addonOptions = addons?.map(addon => ({
        value: addon._id,
        label: `${addon.title} ${addon.status === "ACTIVE" ? "✅" : "⏸️"}`
    }));

    const totalValidIngredients = formData.rawMaterials.filter(
        rm => rm.rawMaterial && rm.quantity && parseFloat(rm.quantity) > 0
    ).length;

    return (
        <div className="allocate-ingredients-step">
            <Modal show={showSkipConfirm} onHide={() => setShowSkipConfirm(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Skip Ingredients</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to skip adding ingredients? You can add them later from the product edit page.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowSkipConfirm(false)}>
                        Cancel
                    </Button>
                    <Button variant="warning" onClick={confirmSkipIngredients}>
                        Yes, Skip Ingredients
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showCompleteConfirm} onHide={() => setShowCompleteConfirm(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Complete Product Creation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Ingredients have been {formData._id ? 'updated' : 'saved'} successfully!</p>
                    <p>Do you want to complete the product creation process?</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCompleteConfirm(false)}>
                        Continue Editing
                    </Button>
                    <Button variant="success" onClick={confirmComplete}>
                        Yes, Complete Creation
                    </Button>
                </Modal.Footer>
            </Modal>

            <h6 className="mb-4 text-primary">Step 4: Allocate Ingredients (Optional)</h6>

            {!productData._id && (
                <Alert variant="warning" className="mb-4">
                    <Alert.Heading>Product Not Created Yet</Alert.Heading>
                    <p>Please go back to Step 1 and create the product first to get the Product ID.</p>
                </Alert>
            )}

            <Card className="mb-4">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                            <h6 className="mb-0 fw-semibold">Product Information</h6>
                            <p className="text-muted mb-0">{productData.name}</p>
                        </div>
                        <div>
                            {existingIngredients ? (
                                <Badge bg="info">
                                    <i className="ri-edit-line me-1"></i> Edit Existing Ingredients
                                </Badge>
                            ) : (
                                <Badge bg="primary">
                                    <i className="ri-add-line me-1"></i> Add New Ingredients
                                </Badge>
                            )}
                        </div>
                    </div>

                    <Row>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Product Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={productData.name || "Not created yet"}
                                    readOnly
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Status</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={existingIngredients ? "Ingredients exist - You can update them" : "No ingredients yet - Add new ones"}
                                    readOnly
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card>
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                            <h6 className="mb-0 fw-semibold">
                                {existingIngredients ? 'Update Ingredients' : 'Add Ingredients'} (Optional)
                            </h6>
                            <p className="text-muted mb-0">You can skip this step and add ingredients later</p>
                        </div>
                        <div>
                            <Badge bg={totalValidIngredients > 0 ? "success" : "secondary"}>
                                {totalValidIngredients} valid ingredient(s)
                            </Badge>
                            {existingIngredients && (
                                <Badge bg="warning" className="ms-2">
                                    Editing
                                </Badge>
                            )}
                        </div>
                    </div>

                    {isLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2">Loading existing ingredients...</p>
                        </div>
                    ) : (
                        <>
                            {formData.rawMaterials.map((entry, index) => {
                                const selectedAddon = addons?.find(a => a?._id === entry.addonId);

                                return (
                                    <Card key={index} className="mb-4 border">
                                        <Card.Body className="bg-light">
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <Badge bg="secondary">Material #{index + 1}</Badge>
                                                {formData.rawMaterials.length > 1 && (
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => removeRawMaterialEntry(index)}
                                                    >
                                                        <i className="ri-delete-bin-line me-1"></i>Remove
                                                    </Button>
                                                )}
                                            </div>

                                            <Row className="mb-3">
                                                <Col md={4}>
                                                    <Form.Group>
                                                        <Form.Label>
                                                            Raw Material <span className="text-danger">*</span>
                                                        </Form.Label>
                                                        <Select
                                                            options={rawMaterialOptions}
                                                            value={
                                                                entry.rawMaterial
                                                                    ? {
                                                                        value: entry.rawMaterial,
                                                                        label:
                                                                            rawMaterialOptions.find(
                                                                                (rm) => rm.value === entry.rawMaterial
                                                                            )?.label || "Selected Raw Material"
                                                                    }
                                                                    : null
                                                            }
                                                            onChange={(selectedOption) =>
                                                                handleRawMaterialChange(
                                                                    index,
                                                                    "rawMaterial",
                                                                    selectedOption ? selectedOption.value : ""
                                                                )
                                                            }
                                                            placeholder="Select Raw Material"
                                                            classNamePrefix="react-select"
                                                            isClearable
                                                        />
                                                    </Form.Group>
                                                </Col>

                                                <Col md={2}>
                                                    <Form.Group>
                                                        <Form.Label>
                                                            Quantity <span className="text-danger">*</span>
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            step="0.001"
                                                            value={entry.quantity || ''}
                                                            onChange={(e) =>
                                                                handleRawMaterialChange(index, "quantity", e.target.value)
                                                            }
                                                            placeholder="0.00"
                                                        />
                                                    </Form.Group>
                                                </Col>

                                                <Col md={2}>
                                                    <Form.Group>
                                                        <Form.Label>Unit</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            value={entry.unit || ''}
                                                            readOnly
                                                            placeholder="Auto-filled"
                                                        />
                                                    </Form.Group>
                                                </Col>

                                                <Col md={2} className="d-flex flex-column justify-content-center">
                                                    <Form.Check
                                                        type="checkbox"
                                                        id={`isImportant-${index}`}
                                                        label="Important"
                                                        checked={entry.isImportant || false}
                                                        onChange={(e) =>
                                                            handleRawMaterialChange(index, "isImportant", e.target.checked)
                                                        }
                                                        className="mb-2"
                                                    />
                                                    <Form.Check
                                                        type="checkbox"
                                                        id={`isOptional-${index}`}
                                                        label="Optional"
                                                        checked={entry.isOptional || false}
                                                        onChange={(e) =>
                                                            handleRawMaterialChange(index, "isOptional", e.target.checked)
                                                        }
                                                    />
                                                </Col>

                                                <Col md={2} className="d-flex align-items-end justify-content-end">
                                                    <div className="text-muted small text-end">
                                                        {entry.isImportant && <div>Marked important</div>}
                                                        {entry.isOptional && <div>Marked optional</div>}
                                                    </div>
                                                </Col>
                                            </Row>

                                            <Row className="mb-3">
                                                <Col md={6}>
                                                    <Form.Group>
                                                        <Form.Label>Addon Group (Optional)</Form.Label>
                                                        <Select
                                                            options={addonOptions}
                                                            value={
                                                                entry.addonId
                                                                    ? {
                                                                        value: entry.addonId,
                                                                        label:
                                                                            addonOptions.find(a => a.value === entry.addonId)?.label ||
                                                                            "Selected Addon Group"
                                                                    }
                                                                    : null
                                                            }
                                                            onChange={(selectedOption) =>
                                                                handleRawMaterialChange(
                                                                    index,
                                                                    "addonId",
                                                                    selectedOption ? selectedOption.value : ""
                                                                )
                                                            }
                                                            placeholder="Select Addon Group"
                                                            classNamePrefix="react-select"
                                                            isClearable
                                                        />
                                                    </Form.Group>
                                                </Col>

                                                <Col md={6}>
                                                    <Form.Group>
                                                        <Form.Label>Addon Value (Optional)</Form.Label>
                                                        <Select
                                                            options={getAddonValueOptions(entry.addonId)}
                                                            value={
                                                                entry.addonValueId && entry.addonId
                                                                    ? {
                                                                        value: entry.addonValueId,
                                                                        label:
                                                                            getAddonValueOptions(entry.addonId).find(
                                                                                v => v.value === entry.addonValueId
                                                                            )?.label || "Unknown Value"
                                                                    }
                                                                    : null
                                                            }
                                                            onChange={(selectedOption) =>
                                                                handleRawMaterialChange(
                                                                    index,
                                                                    "addonValueId",
                                                                    selectedOption ? selectedOption.value : ""
                                                                )
                                                            }
                                                            placeholder={
                                                                entry.addonId
                                                                    ? "Select Addon Value"
                                                                    : "Select Addon Group first"
                                                            }
                                                            classNamePrefix="react-select"
                                                            isClearable
                                                            isDisabled={!entry.addonId}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>

                                            {entry.addonId && entry.addonValueId && (
                                                <Card className="border mt-3">
                                                    <Card.Body className="bg-white">
                                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                                            <div>
                                                                <h6 className="mb-0">Conditional Addons</h6>
                                                                <small className="text-muted">
                                                                    Add conditions for this raw material based on addon selection
                                                                </small>
                                                            </div>
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={() => addConditionalAddon(index)}
                                                            >
                                                                <i className="ri-add-line me-1"></i>Add Condition
                                                            </Button>
                                                        </div>

                                                        {(entry.conditionalAddons || []).length === 0 && (
                                                            <div className="text-muted small">
                                                                No conditional addons added yet.
                                                            </div>
                                                        )}

                                                        {(entry.conditionalAddons || []).map((cond, condIndex) => {
                                                            const condAddon = addons?.find((a) => a?._id === cond.addonId);

                                                            return (
                                                                <Card key={condIndex} className="mb-3 border">
                                                                    <Card.Body className="bg-light">
                                                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                                                            <Badge bg="dark">
                                                                                Condition #{condIndex + 1}
                                                                            </Badge>
                                                                            <Button
                                                                                variant="outline-danger"
                                                                                size="sm"
                                                                                onClick={() =>
                                                                                    removeConditionalAddon(index, condIndex)
                                                                                }
                                                                            >
                                                                                Remove
                                                                            </Button>
                                                                        </div>

                                                                        <Row>
                                                                            <Col md={4}>
                                                                                <Form.Group>
                                                                                    <Form.Label>
                                                                                        Addon Group <span className="text-danger">*</span>
                                                                                    </Form.Label>
                                                                                    <Select
                                                                                        options={addonOptions}
                                                                                        value={
                                                                                            cond.addonId
                                                                                                ? {
                                                                                                    value: cond.addonId,
                                                                                                    label:
                                                                                                        addonOptions.find(
                                                                                                            (a) => a.value === cond.addonId
                                                                                                        )?.label || "Selected Addon Group"
                                                                                                }
                                                                                                : null
                                                                                        }
                                                                                        onChange={(selectedOption) =>
                                                                                            handleConditionalAddonChange(
                                                                                                index,
                                                                                                condIndex,
                                                                                                "addonId",
                                                                                                selectedOption?.value || ""
                                                                                            )
                                                                                        }
                                                                                        classNamePrefix="react-select"
                                                                                        isClearable
                                                                                        placeholder="Select Addon Group"
                                                                                    />
                                                                                </Form.Group>
                                                                            </Col>

                                                                            <Col md={4}>
                                                                                <Form.Group>
                                                                                    <Form.Label>
                                                                                        Addon Value <span className="text-danger">*</span>
                                                                                    </Form.Label>
                                                                                    <Select
                                                                                        options={
                                                                                            condAddon?.addonValues?.map((val) => ({
                                                                                                value: val._id,
                                                                                                label: val.value,
                                                                                            })) || []
                                                                                        }
                                                                                        value={
                                                                                            cond.addonValueId && cond.addonId
                                                                                                ? {
                                                                                                    value: cond.addonValueId,
                                                                                                    label:
                                                                                                        condAddon?.addonValues?.find(
                                                                                                            (v) => v._id === cond.addonValueId
                                                                                                        )?.value || "Selected Addon Value"
                                                                                                }
                                                                                                : null
                                                                                        }
                                                                                        onChange={(selectedOption) =>
                                                                                            handleConditionalAddonChange(
                                                                                                index,
                                                                                                condIndex,
                                                                                                "addonValueId",
                                                                                                selectedOption?.value || ""
                                                                                            )
                                                                                        }
                                                                                        classNamePrefix="react-select"
                                                                                        isClearable
                                                                                        placeholder={
                                                                                            cond.addonId
                                                                                                ? "Select Addon Value"
                                                                                                : "Select Addon Group first"
                                                                                        }
                                                                                        isDisabled={!cond.addonId}
                                                                                    />
                                                                                </Form.Group>
                                                                            </Col>

                                                                            <Col md={4}>
                                                                                <Form.Group>
                                                                                    <Form.Label>
                                                                                        Quantity <span className="text-danger">*</span>
                                                                                    </Form.Label>
                                                                                    <div className="input-group">
                                                                                        <Form.Control
                                                                                            type="number"
                                                                                            placeholder="Enter quantity"
                                                                                            value={cond.quantity}
                                                                                            onChange={(e) =>
                                                                                                handleConditionalAddonChange(
                                                                                                    index,
                                                                                                    condIndex,
                                                                                                    "quantity",
                                                                                                    e.target.value
                                                                                                )
                                                                                            }
                                                                                        />
                                                                                        <span className="input-group-text">
                                                                                            {entry.unit || "Unit"}
                                                                                        </span>
                                                                                    </div>
                                                                                </Form.Group>
                                                                            </Col>
                                                                        </Row>
                                                                    </Card.Body>
                                                                </Card>
                                                            );
                                                        })}
                                                    </Card.Body>
                                                </Card>
                                            )}
                                        </Card.Body>
                                    </Card>
                                );
                            })}

                            <div className="text-center mt-3">
                                <Button
                                    variant="outline-primary"
                                    onClick={addRawMaterialEntry}
                                    className="me-2"
                                >
                                    <i className="ri-add-line me-1"></i> Add More Ingredients
                                </Button>
                            </div>
                        </>
                    )}

                    <Alert variant="info" className="mt-4">
                        <Alert.Heading>Note:</Alert.Heading>
                        <div className="mb-0">
                            <p>
                                {existingIngredients ? (
                                    <>You are updating existing ingredients. Changes will be saved immediately.</>
                                ) : (
                                    <>You are adding new ingredients. They will be saved when you click "Save Ingredients".</>
                                )}
                            </p>
                            <ul className="mb-0">
                                <li>Mark ingredients as "Important" if they are critical to the recipe</li>
                                <li>Mark ingredients as "Optional" if they are not always required</li>
                                <li>Addon fields are optional</li>
                                <li>If an addon group is selected, you must also select an addon value</li>
                                <li>Conditional addons can be added only after selecting addon group and addon value</li>
                            </ul>
                        </div>
                    </Alert>
                </Card.Body>
            </Card>

            <div className="mt-4 d-flex justify-content-between">
                <Button
                    variant="outline-secondary"
                    onClick={onBack}
                    disabled={isSaving || isSubmitting || isUpdating}
                >
                    ← Back to Add-ons
                </Button>

                <div>
                    <Button
                        variant="outline-warning"
                        onClick={handleSkipIngredients}
                        disabled={isSaving || isSubmitting || isUpdating || !productId}
                        className="me-3"
                    >
                        Skip Ingredients
                    </Button>

                    <Button
                        variant={existingIngredients ? "primary" : "success"}
                        onClick={handleSaveOrUpdateIngredients}
                        disabled={isSaving || isSubmitting || isUpdating || !productId}
                    >
                        {isUpdating ? (
                            <>
                                <Spinner size="sm" animation="border" className="me-2" />
                                {existingIngredients ? 'Updating...' : 'Saving...'}
                            </>
                        ) : (
                            existingIngredients ? 'Update Ingredients' : 'Save Ingredients'
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default AllocateIngredients;